/**
 * The analysis layers' entry point for the editor: one hook that owns the store, fetches the layers when a layer
 * first needs them, derives the per-panel numbers of the draft, answers the layer hotkeys, and hands TopView and
 * ThreeView what they draw. Editor.tsx only wires these in (keeps the shared file's changes small).
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { errorMessage, type S } from "../../api/client";
import type { Vec2 } from "../geo.ts";
import { panelGeometry } from "../layout.ts";
import type { Draft } from "../model.ts";
import type { LiveRoof } from "../roof.ts";
import type { ModuleSize } from "../snap.ts";
import type { ThreeScene } from "../ThreeView";
import { useAnalysisData } from "./data.ts";
import type { DockApi } from "./Dock";
import { AnalysisOverlay, AnalysisUnderlay } from "./Overlay2D";
import { panelYields, type PanelYield } from "./panels.ts";
import { createAnalysisStore, needsData, stepDay, stepMinutes, stepMonth, toggleRaster, useAnalysisState, type AnalysisStore } from "./store.ts";

export interface EditorAnalysis {
  store: AnalysisStore;
  data: ReturnType<typeof useAnalysisData>["data"];
  loading: boolean;
  error: string | null;
  yields: PanelYield[] | null;
  dockRef: React.MutableRefObject<DockApi | null>;
  /** Handles a layer hotkey; false when the action is not one of the layers'. */
  run: (actionId: string, view: "2d" | "3d") => boolean;
  underlay: (mpp: number) => ReactNode;
  overlay: (mpp: number) => ReactNode;
  onHover: (p: Vec2 | null) => void;
  onSection: (line: { a: Vec2; b: Vec2 } | null) => void;
  scene: ThreeScene | null;
  onScene: (scene: ThreeScene | null) => void;
}

export function useEditorAnalysis(opts: {
  designId: string;
  doc: S["EditorDocument"];
  roof: LiveRoof;
  draft: Draft;
  module: ModuleSize;
  reportOpen: boolean;
}): EditorAnalysis {
  const { designId, doc, roof, draft, module, reportOpen } = opts;
  const store = useMemo(() => createAnalysisStore(), []);
  const wanted = useAnalysisState(store, (s) => needsData(s, reportOpen));
  // once fetched, keep it (turning a layer off and on again is instant)
  const [everWanted, setEverWanted] = useState(false);
  if (wanted && !everWanted) setEverWanted(true);
  const { data, loading, error } = useAnalysisData(designId, doc.version_number, wanted || everWanted);
  const dockRef = useRef<DockApi | null>(null);
  const [scene, setScene] = useState<ThreeScene | null>(null);

  const yields = useMemo(() => {
    if (!data) return null;
    const panels = draft.panels.map((item) => ({ item, plan: panelGeometry(item, roof, module).plan }));
    return panelYields(panels, doc.panels, doc.module?.watts ?? 0, data);
  }, [data, draft.panels, roof, module, doc.panels, doc.module]);

  const run = useCallback(
    (id: string, view: "2d" | "3d"): boolean => {
      const set = store.set;
      switch (id) {
        case "layer.height":
          // in 3D the key shows the DSM as a height-coloured point cloud
          if (view === "3d") set((s) => ({ cloud: s.cloud === "off" ? "points" : "off" }));
          else set((s) => toggleRaster(s, "height"));
          return true;
        case "layer.contours": set((s) => ({ contours: !s.contours, raster: "height" })); return true;
        case "layer.irradiance": set((s) => toggleRaster(s, "irradiance")); return true;
        case "layer.monthPrev": set((s) => stepMonth(s, -1)); return true;
        case "layer.monthNext": set((s) => stepMonth(s, 1)); return true;
        case "layer.access": set((s) => toggleRaster(s, "access")); return true;
        case "layer.tsrf": set((s) => toggleRaster(s, "tsrf")); return true;
        case "layer.kwh": set((s) => ({ panelKwh: !s.panelKwh })); return true;
        case "layer.sun": set((s) => ({ sun: !s.sun, playing: false })); return true;
        case "sun.play": set((s) => ({ sun: true, playing: !s.playing })); return true;
        case "sun.earlier": set((s) => ({ sun: true, playing: false, ...stepMinutes(s, -15) })); return true;
        case "sun.later": set((s) => ({ sun: true, playing: false, ...stepMinutes(s, 15) })); return true;
        case "sun.dayBack": set((s) => ({ sun: true, ...stepDay(s, -7) })); return true;
        case "sun.dayOn": set((s) => ({ sun: true, ...stepDay(s, 7) })); return true;
        default: return false;
      }
    },
    [store],
  );

  const underlay = useCallback((_mpp: number) => <AnalysisUnderlay data={data} store={store} />, [data, store]);
  const overlay = useCallback((mpp: number) => <AnalysisOverlay store={store} yields={yields} mpp={mpp} />, [store, yields]);
  const onHover = useCallback((p: Vec2 | null) => dockRef.current?.hover(p), []);
  const onSection = useCallback((line: { a: Vec2; b: Vec2 } | null) => store.set({ section: line }), [store]);

  return {
    store,
    data,
    loading,
    error: error ? errorMessage(error) : null,
    yields,
    dockRef,
    run,
    underlay,
    overlay,
    onHover,
    onSection,
    scene,
    onScene: setScene,
  };
}
